<script lang="ts">
	import { relatedUrlList, caseStudyArticlesList, caseStudyArticlesReviews, services, expertises, benefits, approach } from '$lib/config/telemedicine';
	import { domain, openGraphImageUrl } from '$lib/config/system';
	import { articles, blogs, technologysStack } from '$lib/config/shared';
	import { organizationSchema, webSiteSchema, createProductSchema } from '$lib/config/schemas';
	import { sortArticlesByUrlList } from '$lib/helpers/sort-articles.helper';
	import { serializeSchema } from '$lib/helpers/json-ld.helper';
	import type { SeoConfig } from '$lib/domain/seo-config.interface';

	import ServicesBlock from '$components/ServicesBlock.svelte';
	import ExpertiseBlock from '$components/ExpertiseBlock.svelte';
	import GreenIconBlock from '$components/GreenIconBlock.svelte';
	import TechnologyStackBlock from '$components/TechnologyStackBlock.svelte';
	import CallToActionBlock from '$components/CallToActionBlock.svelte';
	import CaseStudyBlock from '$components/CaseStudyBlock.svelte';
	import Testimonials from '$components/Testimonials.svelte';
	import RelatedArticlesBlock from '$components/RelatedArticlesBlock.svelte';
	import ApproachBlock from '$components/ApproachBlock.svelte';
	import ContactForm from '$components/ContactForm.svelte';

	const slug = 'telemedicine-software-development';
	const seo: SeoConfig = {
		title: 'Telemedicine Software & App Development Company | Exoft',
		description: 'Learn how our telemedicine software and app development services enable e-prescriptions, online appointment booking, and remote monitoring in your clinic.',
		canonical: `${domain}/${slug}/`,
		modifiedTime: '2022-08-27T10:13:18+00:00'
	};

	const caseStudiesArticles = articles.filter((item) => [...caseStudyArticlesList].includes(item.url));

	const relatedCaseStudies = articles.filter((item) => [...relatedUrlList].includes(item.url));

	const relatedBlogs = blogs.filter((item) => [...relatedUrlList].includes(item.url));

	const relatedArticles = sortArticlesByUrlList(relatedUrlList, [...relatedBlogs, ...relatedCaseStudies]);

	const relatedCaseStudyArticles = sortArticlesByUrlList(caseStudyArticlesList, [...caseStudiesArticles]);

	const faqSchema = {
		'@type': 'FAQPage',
		mainEntity: [
			{
				'@type': 'Question',
				name: 'How much does it cost to develop a telemedicine app?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'We ensure cost-efficient and competitive pricing for our telemedicine app development services. It depends on the hourly rates of our telemedicine mobile app developers. Besides, telemedicine app development costs correspond to the number of telemedicine app features you need and their complexity. To get a quote for your telemedicine software development, please provide us with a detailed request.'
				}
			},
			{
				'@type': 'Question',
				name: 'Does a telehealth app need to adhere to the HIPAA regulations?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'Actually, yes. Since your telemedicine app development solution will hold and process patient health information (PHI), it has to be incredibly secure. Thus, your telehealth software development product must follow the HIPAA rules.'
				}
			},
			{
				'@type': 'Question',
				name: 'Are Telehealth and Telemedicine the same?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'Although people often use telehealth and telemedicine as interchangeable terms, they are different. Telehealth covers all healthcare services, including medical education, telecommunication treatment, and remote patient monitoring. Meanwhile, telemedicine means using telecommunication mediums for providing remote patients with the required medical services'
				}
			},
			{
				'@type': 'Question',
				name: 'What are the different types of telemedicine apps?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'Among the most popular telemedicine apps are primary care, urgent care, mental health care, and wellness apps.'
				}
			},
			{
				'@type': 'Question',
				name: 'Does Exoft work on project-based development?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'Our team can not only create a decent custom software solution but also take care of its development process. To make sure that we’re moving in the right direction, we’ll be regularly getting in touch with you and, of course, you’ll have an opportunity to check project progress at any time.'
				}
			},
			{
				'@type': 'Question',
				name: 'Should I choose a ready-made telemedicine product or instead create a custom one?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'If healthcare providers require a telemedicine platform with basic functions without different integrations with other app development solutions, they should choose a ready-made telemedicine app. But if you desire to have robust features like enhanced data analytics, health insurance portability, video conferencing, or integrations with various app development products, you should build a telemedicine platform from scratch.'
				}
			}
		]
	};
	const productInfo = {
		name: 'Telemedicine Software & App Development',
		description: seo.description,
		image: domain + '/landing/' + slug + '.webp',
		rating: 4.8,
		reviews: 825
	};
	const productSchema = createProductSchema(productInfo);
	const combinedSchemas = serializeSchema(faqSchema, productSchema, organizationSchema, webSiteSchema);
</script>

<svelte:head>
	<title>{seo.title}</title>
	<link rel="canonical" href="{seo.canonical}" />
	<meta name="description" content="{seo.description}" />
	<meta property="og:locale" content="en_US" />
	<meta property="og:type" content="website" />
	<meta property="og:title" content="{seo.title}" />
	<meta property="og:description" content="{seo.description}" />
	<meta property="og:url" content="{seo.canonical}" />
	<meta property="og:site_name" content="Exoft" />
	<meta property="article:modified_time" content="{seo.modifiedTime}" />
	<meta property="og:image" content="{domain + '/landing/' + slug + '.webp'}" />
	<meta property="og:image:width" content="1440" />
	<meta property="og:image:height" content="850" />
	<meta property="og:image:type" content="image/webp" />
	<meta property="og:image:alt" content="{seo.title}" />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:site" content="Exoft" />
	<meta name="twitter:title" content="{seo.title}" />
	<meta name="twitter:description" content="{seo.description}" />
	<meta name="twitter:image" content="{openGraphImageUrl}" />
	<link rel="preload" href="/landing/{slug}.webp" as="image" />

	<style>
		.top-page-bg {
			background: url(/landing/telemedicine-software-development.webp) top/cover no-repeat;
		}
	</style>
</svelte:head>

<section>
	<div class="root flex-column align-center">
		<section class="top-page-bg">
			<div class="container top-page">
				<h1>Telemedicine App Development Services</h1>
				<p class="h5">
					We build robust telemedicine platforms to securely connect patients and clinicians at a distance. Rely on our telemedicine software development expertise to transform the way your healthcare organization delivers medical services.
				</p>
				<a href="{domain}/contact-us/" type="button" class="action-button">Talk to an expert</a>
			</div>
		</section>
		
		<section>
			<ServicesBlock
				title="{'Our Telemedicine Software Development Services'}"
				description="{'Offering a wide range of telemedicine app development services, we can be your partner for all your technical needs. Our team is ready to handle any request, from building a telemedicine solution from scratch to providing ongoing support.'}"
				data="{services}"
			/>
		</section>
		
		<section class="no-paddings">
			<ExpertiseBlock
				title="{'Our Expertise in Telemedicine Software Solutions'}"
				description="{'Our telemedicine app development company is well-equipped to create the perfect solution for your clinic or hospital. In particular, we specialize in building the following functionalities:'}"
				data="{expertises}"
			/>
		</section>
		
		<section>
			<GreenIconBlock
				title="{'Why Choose Exoft for Your Telemedicine Software Development'}"
				description="{'We\'ve teamed up with dozens of businesses worldwide, providing high-quality custom software tailored to their needs. Here are the advantages we offer as a telemedicine app development company:'}"
				data="{benefits}"
			/>
		</section>
		
		<section class="no-paddings">
			<TechnologyStackBlock
				title="{'Technologies We Work With'}"
				description="{'A modern tech stack we use in telemedicine app development services allows our team to build reliable solutions aligned with client needs, industry regulations, and strict security standards.'}"
				data="{technologysStack}"
			/>
		</section>
		
		<section class="no-paddings">
			<CallToActionBlock
				title="{'Looking for a reliable and experienced telemedicine app development company?'}"
				description="{'With numerous telemedicine software development projects in our portfolio, we’re well-equipped to build a top-notch custom solution for your medical organization, no matter the complexity.'}"
				action="{'Send a request'}"
			/>
		</section>
		
		<section>
			<CaseStudyBlock
				title="{'Case Studies'}"
				explicitArticles="{relatedCaseStudyArticles}"
				targetReviews="{caseStudyArticlesReviews}"
			/>
		</section>
		
		<section class="no-paddings">
			<Testimonials
				description="{'Since 2013, we have completed 500+ projects for clients worldwide, spanning the US, Canada, the EU, and Australia. While we\'ve partnered with businesses in various sectors, our primary focus is the healthcare industry.<br><br>Hospitals and clinics opt for our telemedicine application development services for several reasons. First, our expertise covers a wide range of telemedicine needs. Second, we prioritize client needs above all. Last but not least, our deep industry knowledge helps us navigate stringent medical, legal, and security requirements.<br><br>Our team has received dozens of positive reviews, and we’re proud to have a 95% customer satisfaction rate.'}"
				iconUrl="{'/reviews/boris.webp'}"
				author="{'Boris Jinjolava, CEO at ViCLinic Inc.'}"
				text="{'«Exoft has successfully supported us to push our business forward.<br> They bounce ideas off each other and give feedback and context to help in tech areas. Moreover, theyre approachable, responsive partners, and the team stands out for their expertise. It feels more like an internal team than an outsourcing relationship.<br> Thank you...»'}"
			/>
		</section>
		
		<section>
			<RelatedArticlesBlock
				articles="{relatedArticles}"
			/>
		</section>
		
		<section class="no-paddings">
			<ApproachBlock
				title="{'Our approach in Telehealth App Development Services'}"
				data="{approach}"
			/>
		</section>

		<section>
			<ContactForm />
		</section>
	</div>
</section>
{@html combinedSchemas}
